import { useNavigate } from 'react-router-dom';

type InfluencerCardProps = {
  id: string;
  name: string;
  photo: string | null;
  country: string | null;
  niche: string | null;
  languages: string | null;
  followers: number | null;
};

// Mapping of country codes to flag emojis
const countryFlags: { [key: string]: string } = {
  'US': '🇺🇸', 'GB': '🇬🇧', 'PL': '🇵🇱',
  'FR': '🇫🇷', 'DE': '🇩🇪', 'IT': '🇮🇹', 'ES': '🇪🇸',
  'JP': '🇯🇵', 'CN': '🇨🇳', 'IN': '🇮🇳', 'BR': '🇧🇷',
  'CA': '🇨🇦', 'AU': '🇦🇺', 'MX': '🇲🇽', 'KR': '🇰🇷',
};

const getCountryCode = (country: string | null): string => {
  if (!country) return '';
  return country.substring(0, 2).toUpperCase();
};

export default function InfluencerCard({
  id,
  name,
  photo,
  country,
  niche,
  languages,
  followers
}: InfluencerCardProps) {
  const navigate = useNavigate();
  const countryCode = getCountryCode(country);
  const flag = countryFlags[countryCode] || '';
  
  const languageList = languages 
    ? languages.split(',').slice(0, 2).map(l => l.trim())
    : [];

  return (
    <div 
      onClick={() => navigate(`/creator/${id}`)}
      className="bg-white border border-gray-200 hover:border-black transition-all hover:shadow-md cursor-pointer group"
    >
      {/* Photo Section */}
      <div className="aspect-square overflow-hidden bg-gray-100">
        {photo ? (
          <img 
            src={photo} 
            alt={`${name} - Travel Creator`}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-5xl font-bold text-gray-300" aria-hidden="true">{name.charAt(0)}</span>
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="p-4 space-y-2">
        {/* Name */}
        <h3 className="font-semibold text-sm line-clamp-2">{name}</h3>

        {/* Country with Flag */}
        <div className="flex items-center gap-2 text-xs text-gray-600">
          {flag && <span>{flag}</span>}
          <span>{country || 'Unknown'}</span>
        </div>

        {/* Niche */}
        {niche && (
          <p className="text-xs text-gray-500">{niche}</p>
        )}

        {/* Languages */}
        {languageList.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-2">
            {languageList.map((lang) => (
              <span key={lang} className="inline-block bg-gray-100 px-2 py-1 text-xs rounded">
                {lang}
              </span>
            ))}
          </div>
        )}

        {/* Followers */}
        {followers !== null && followers !== undefined && (
          <p className="text-xs text-gray-600 pt-1">
            {followers.toLocaleString()} followers
          </p>
        )}
      </div>
    </div>
  );
}
